import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { of } from 'rxjs';
import { catchError, tap, timeout } from 'rxjs/operators';
import { firstValueFrom } from 'rxjs';
import { BrowserService } from './browser.service';

@Injectable({
  providedIn: 'root',
})
export class IpService {
  private cachedIp: string | null = null;
  private ipFetchPromise: Promise<string | null> | null = null;

  constructor(
    private http: HttpClient,
    private browserService: BrowserService,
  ) {}

  /**
   * Get client IP address
   * Uses cached value if available, otherwise fetches from service
   */
  getClientIp(): Promise<string | null> {
    // Return cached IP if available
    if (this.cachedIp) {
      return Promise.resolve(this.cachedIp);
    }

    // Return existing promise if fetch is in progress
    if (this.ipFetchPromise) {
      return this.ipFetchPromise;
    }

    // Only fetch in browser environment
    if (!this.browserService.checkBrowser()) {
      return Promise.resolve(null);
    }

    // Fetch IP from service
    this.ipFetchPromise = this.fetchIpAddress();
    return this.ipFetchPromise;
  }

  /**
   * Fetch IP address from external service
   */
  private async fetchIpAddress(): Promise<string | null> {
    try {
      // Using ipify.org as a free IP service
      // Alternative: You can use your own backend endpoint that returns the IP
      const response = await firstValueFrom(
        this.http.get<{ ip: string }>('https://api.ipify.org?format=json').pipe(
          timeout(5000), // 5 second timeout
          catchError((error) => {
            // Log detailed error for debugging
            console.warn('Failed to fetch client IP address:', {
              message: error?.message || 'Unknown error',
              status: error?.status,
              statusText: error?.statusText,
              url: error?.url,
              error: error
            });
            return of({ ip: '' } as { ip: string });
          }),
        ),
      );

      // Cache and return the IP if available
      if (response && response.ip) {
        this.cachedIp = response.ip;
        return this.cachedIp;
      }

      return null;
    } catch (error) {
      // Ensure we return null on any error
      console.warn('Error fetching IP address:', error);
      return null;
    } finally {
      // Clear the promise so it can be retried if needed
      this.ipFetchPromise = null;
    }
  }

  /**
   * Clear cached IP (useful for testing or when IP might change)
   */
  clearCache(): void {
    this.cachedIp = null;
    this.ipFetchPromise = null;
  }
}
